import { adminConfig } from './config';
import type { AnalyticsOverview } from '../analytics/types';
import type {
  AdminSession,
  NotificationDispatchRequest,
  NotificationDispatchResponse,
  RegisteredPushDevicesResponse,
} from '../types';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detail: unknown,
  ) {
    super(message);
  }
}

export async function createDispatch(
  token: string,
  payload: NotificationDispatchRequest,
): Promise<NotificationDispatchResponse> {
  return requestJson('/v1/admin/notifications/dispatches', token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getDispatch(token: string, dispatchId: number): Promise<NotificationDispatchResponse> {
  return requestJson(`/v1/admin/notifications/dispatches/${dispatchId}`, token, {
    method: 'GET',
  });
}

export async function getAdminSession(token: string): Promise<AdminSession> {
  return requestJson('/v1/admin/notifications/session', token, {
    method: 'GET',
  });
}

export async function listRegisteredDevices(token: string): Promise<RegisteredPushDevicesResponse> {
  return requestJson('/v1/admin/notifications/devices', token, {
    method: 'GET',
  });
}

export type AnalyticsRange = { days: number } | { start: string; end: string };

export async function getAnalyticsOverview(
  token: string,
  range: AnalyticsRange,
  refresh = false,
): Promise<AnalyticsOverview> {
  const query = new URLSearchParams('days' in range ? { days: String(range.days) } : { start: range.start, end: range.end });
  if (refresh) query.set('refresh', 'true');
  return requestJson(`/v1/admin/analytics/overview?${query.toString()}`, token, {
    method: 'GET',
  });
}

async function requestJson<T>(path: string, token: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${adminConfig.apiBaseUrl.replace(/\/$/, '')}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...init.headers,
    },
  });

  if (!response.ok) {
    const detail = await readErrorDetail(response);
    throw new ApiError(formatError(detail, response.statusText), response.status, detail);
  }

  return response.json() as Promise<T>;
}

async function readErrorDetail(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return response.statusText;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function formatError(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') {
    return detail;
  }

  if (detail && typeof detail === 'object' && 'detail' in detail) {
    const nestedDetail = (detail as { detail: unknown }).detail;
    if (typeof nestedDetail === 'string') {
      return nestedDetail;
    }

    if (Array.isArray(nestedDetail)) {
      return nestedDetail.map(formatValidationIssue).join('\n');
    }

    return JSON.stringify(nestedDetail);
  }

  return fallback;
}

function formatValidationIssue(issue: unknown): string {
  if (!issue || typeof issue !== 'object') {
    return String(issue);
  }

  const record = issue as { loc?: unknown; msg?: unknown; type?: unknown };
  const loc = Array.isArray(record.loc) ? record.loc.filter((part) => part !== 'body').join('.') : '';
  const msg = typeof record.msg === 'string' ? record.msg : JSON.stringify(record);
  const type = typeof record.type === 'string' ? ` (${record.type})` : '';
  return loc ? `${loc}: ${msg}${type}` : `${msg}${type}`;
}